import { ApolloError } from '@apollo/client';

import {
  useErpFundingCandidateKfsBasisQuery,
  useErpFundingCandidateQuery,
  useErpFundingUserSearchQuery,
  useErpOperatorsForCandidateQuery,
} from 'core/graphql';

import { useStableRefetch } from '../Groups/useStableRefetch';

/**
 * ТЗ 0024 — данные карточки кандидата, подбора оператора и поиска аккаунта.
 *
 * Все три запроса до этого существовали, были сгенерированы в хуки и не
 * вызывались ниоткуда — то есть анкета, история переходов и вся логика
 * подбора хранились, отдавались по сети и были недостижимы из интерфейса.
 */
export const useFundingCandidate = (id?: string) => {
  const { data, loading, error, refetch } = useErpFundingCandidateQuery({
    variables: { id: id || '' },
    skip: !id,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  return {
    candidate: data?.fundingCandidate ?? null,
    loading,
    error: error as ApolloError | undefined,
    refetch: useStableRefetch(refetch),
  };
};

/**
 * Подбор оператора. Жёсткие критерии применяет сервер; экран обязан показать,
 * ПО КАКОЙ географии шло сужение — для BUR это прописка, для KFS повят
 * работодателя, и это не деталь, а суть правила.
 */
export const useOperatorsForCandidate = (candidateId?: string) => {
  const { data, loading, error } = useErpOperatorsForCandidateQuery({
    variables: { candidateId: candidateId || '' },
    skip: !candidateId,
    fetchPolicy: 'cache-and-network',
  });

  return {
    operators: data?.operatorsForCandidate ?? [],
    loading,
    error: error as ApolloError | undefined,
  };
};

/**
 * #141 — основание подбора KFS (`employer` | `residence_fallback` | …) для
 * окна разбора заявки: там нет полной карточки, а пометить догадку «по месту
 * жительства» нужно так же, как в панели подбора.
 */
export const useCandidateKfsBasis = (candidateId?: string) => {
  const { data } = useErpFundingCandidateKfsBasisQuery({
    variables: { id: candidateId || '' },
    skip: !candidateId,
    fetchPolicy: 'cache-and-network',
  });
  return data?.fundingCandidate?.kfsGeoBasis ?? '';
};

/**
 * Поиск существующего аккаунта для ручной привязки.
 *
 * `skip` до трёх символов: запрос без фильтра вернул бы срез всей таблицы
 * пользователей, а это лишняя выдача персональных данных на каждый набор
 * символа.
 */
export const useFundingUserSearch = (term: string) => {
  const trimmed = term.trim();
  const looksLikeEmail = trimmed.includes('@');
  const { data, loading } = useErpFundingUserSearchQuery({
    variables: {
      email: looksLikeEmail ? trimmed : undefined,
      lastName: looksLikeEmail ? undefined : trimmed,
    },
    skip: trimmed.length < 3,
    fetchPolicy: 'cache-and-network',
  });

  return {
    users: (data?.users?.edges ?? []).map((edge) => edge?.node).filter(Boolean),
    loading,
    tooShort: trimmed.length > 0 && trimmed.length < 3,
  };
};
